import type { MultilineElementTransformer } from '@lexical/markdown';
import { ElementNode, LexicalNode, $createParagraphNode } from 'lexical';
import {
  TableNode,
  TableRowNode,
  TableCellNode,
  $createTableNode,
  $createTableRowNode,
  $createTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableCellNode,
} from '@lexical/table';
import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown';

const importTable = (lines: string[]): TableNode => {
  const table = $createTableNode();

  lines
    .filter((l) => /^\s*\|/.test(l))
    .forEach((line, rIdx) => {
      if (rIdx === 1 && /^\s*\|?\s*[-:| ]+\|?\s*$/.test(line)) {
        return;
      }

      const row = $createTableRowNode();

      line
        .split('|')
        .slice(1, -1)
        .forEach((cell) => {
          const cellNode = $createTableCellNode();
          const p = $createParagraphNode();

          $convertFromMarkdownString(cell.trim(), TRANSFORMERS, p);
          cellNode.append(p);
          row.append(cellNode);
        });

      table.append(row);
    });

  return table;
};

const exportTable = (
  node: TableNode,
  exportChildren: (n: ElementNode) => string,
): string | null => {
  const rows = node.getChildren().filter($isTableRowNode);

  if (rows.length === 0) {
    return '';
  }

  return rows
    .map((row, rowIndex) => {
      const cells = row
        .getChildren()
        .filter($isTableCellNode)
        .map((cell) => {
          const cellContent = exportChildren(cell).replace(/\n+/g, ' ').trim();
          return cellContent || ' ';
        });

      const markdownRow = `| ${cells.join(' | ')} |`;

      if (rowIndex === 0) {
        const headerSeparator = `| ${cells.map(() => '---').join(' | ')} |`;
        return `${markdownRow}\n${headerSeparator}`;
      }

      return markdownRow;
    })
    .join('\n');
};

export const TABLE_TRANSFORMER: MultilineElementTransformer = {
  type: 'multiline-element',
  dependencies: [TableNode, TableRowNode, TableCellNode],
  regExpStart: /^\s*\|/,
  regExpEnd: {
    optional: true,
    regExp: /^\s*$/,
  },
  replace: (
    rootNode: ElementNode,
    children: LexicalNode[] | null,
    startMatch: string[],
    endMatch: string[] | null,
    linesInBetween: string[] | null,
    isImport: boolean,
  ): boolean | void => {
    const lines = startMatch[0].split('\n');

    const table = importTable(lines);
    rootNode.insertAfter(table);
    rootNode.remove();

    return true;
  },
  export: (
    node: LexicalNode,
    exportChildren: (node: ElementNode) => string,
  ): string | null => {
    if (!$isTableNode(node)) {
      return null;
    }

    return exportTable(node, exportChildren);
  },
};

export const TRANSFORMERS_LIST = [...TRANSFORMERS, TABLE_TRANSFORMER];
